<template>
  <div class="rs">
    <header class="rs__head">
      <div>
        <router-link class="rs__back" :to="{ name: 'RegistryMonitor', query: { customer: customerId } }">← Мониторинг реестров</router-link>
        <h1 class="rs__title">Настройки реестров</h1>
      </div>
      <select v-model.number="customerId" class="rs__input" @change="loadCustomer">
        <option v-for="c in customers" :key="c.id" :value="c.id">{{ c.name }}</option>
      </select>
    </header>

    <div v-if="error" class="rs__error">{{ error }}</div>
    <div v-if="message" class="rs__ok">{{ message }}</div>

    <section class="rs__card">
      <h2 class="rs__h2">Условия договора заказчика</h2>
      <p class="rs__muted">К рейсу применяется редакция, действующая на дату погрузки. Новая редакция не затирает старую — история сохраняется.</p>
      <table class="rs__table">
        <thead><tr><th>Договор</th><th>Действует с</th><th>Сроки</th><th>Сверка цены</th><th>Комментарий</th></tr></thead>
        <tbody>
          <tr v-for="t in terms" :key="t.id">
            <td><b>{{ t.contract_name }}</b></td>
            <td>{{ formatDate(t.effective_from) }}</td>
            <td>{{ termsText(t) }}</td>
            <td>допуск {{ t.price_tolerance }} ₽; {{ (t.required_sources || []).map((s) => SOURCE_LABELS[s] || s).join(', ') || 'только ERP' }}</td>
            <td>{{ t.comment || '' }}</td>
          </tr>
          <tr v-if="!terms.length"><td colspan="5" class="rs__muted">Условия не заданы.</td></tr>
        </tbody>
      </table>

      <form v-if="canEdit" class="rs__form" @submit.prevent="addTerms">
        <h3 class="rs__h3">Новая редакция условий</h3>
        <div class="rs__grid">
          <label>Договор <input v-model="draft.contract_name" required placeholder="Эфес с 01.07.2026"></label>
          <label>Действует с <input v-model="draft.effective_from" type="date" required></label>
          <DaysField v-model:days="draft.send_docs_days" v-model:kind="draft.send_docs_kind" label="Отправка проверенного комплекта, от даты реестра" />
          <DaysField v-model:days="draft.review_days" v-model:kind="draft.review_kind" label="Проверка заказчиком (регламент)" />
          <DaysField v-model:days="draft.signing_days" v-model:kind="draft.signing_kind" label="Подписание счёт-фактуры" />
          <DaysField v-model:days="draft.originals_days" v-model:kind="draft.originals_kind" label="Предел сдачи оригиналов, от даты реестра" />
          <DaysField v-model:days="draft.payment_days" v-model:kind="draft.payment_kind" label="Срок оплаты, от даты реестра" />
          <label>Платёжный день (если нет календаря)
            <span class="rs__row">
              <select v-model="nthChoice"><option value="1">1-й</option><option value="1,3">1-й и 3-й</option><option value="2">2-й</option><option value="last">без правила</option></select>
              <select v-model.number="draft.payment_rule.weekday"><option v-for="(d, i) in WEEKDAYS" :key="i" :value="i">{{ d }}</option></select>
            </span>
          </label>
          <label>Допуск расхождения цены, ₽ <input v-model="draft.price_tolerance" type="number" step="0.01" min="0"></label>
          <label>Сверять с
            <span class="rs__row">
              <label class="rs__check"><input v-model="draft.required_sources" type="checkbox" value="tms_efes"> TMS</label>
              <label class="rs__check"><input v-model="draft.required_sources" type="checkbox" value="transmanager"> ТМ</label>
            </span>
          </label>
          <label class="rs__wide">Комментарий <input v-model="draft.comment"></label>
        </div>
        <button class="rs__btn rs__btn--primary" type="submit">Сохранить редакцию</button>
      </form>
    </section>

    <section class="rs__card">
      <h2 class="rs__h2">Плановый календарь «День Х»</h2>
      <p class="rs__muted">Последняя дата загрузки реестра → дата оплаты. Загрузите Excel с листом «День Х» или добавьте дату вручную. Повторная загрузка не создаёт дублей.</p>
      <div v-if="canEdit" class="rs__row">
        <input ref="fileInput" type="file" accept=".xlsx" @change="importFile">
        <span class="rs__muted">или</span>
        <input v-model="manual.last_registry_date" type="date" class="rs__input" title="Последняя дата загрузки реестра">
        <span>→</span>
        <input v-model="manual.payment_date" type="date" class="rs__input" title="Дата оплаты">
        <button class="rs__btn" type="button" :disabled="!manual.last_registry_date || !manual.payment_date" @click="addDay">Добавить</button>
      </div>
      <div class="rs__calendar">
        <div v-for="row in upcoming" :key="row.id" class="rs__day">
          <span>{{ formatDate(row.last_registry_date) }} → <b>{{ formatDate(row.payment_date) }}</b></span>
          <span :class="['rs__term', row.term_days !== draftTerm ? 'rs__term--odd' : '']">{{ row.term_days }} дн.</span>
          <button v-if="canEdit" class="rs__x" type="button" title="Удалить" @click="removeDay(row)">✕</button>
        </div>
      </div>
      <button v-if="calendar.length > upcoming.length || showAll" class="rs__btn rs__btn--sm" type="button" @click="showAll = !showAll">
        {{ showAll ? 'Только будущие' : `Показать все (${calendar.length})` }}
      </button>
    </section>

    <section class="rs__card">
      <h2 class="rs__h2">Общие настройки</h2>
      <div v-if="settings" class="rs__grid">
        <DaysField v-model:days="settings.carrier_docs_days" v-model:kind="settings.carrier_docs_kind" label="Получить документы от перевозчика, от даты разгрузки" />
        <DaysField v-model:days="settings.office_processing_days" v-model:kind="settings.office_processing_kind" label="Обработка документов делопроизводством" />
        <label>Уведомлять за, дней <input v-model.number="settings.warn_before_days" type="number" min="1" max="60"></label>
        <label>Учитывать рейсы с погрузкой с <input v-model="settings.start_date" type="date"></label>
        <label>Рейсы TMS без заказчика относить к
          <select v-model.number="settings.tms_default_customer_id"><option v-for="c in customers" :key="c.id" :value="c.id">{{ c.name }}</option></select>
        </label>
        <label class="rs__check"><input v-model="settings.split_by_fleet" type="checkbox"> Отдельные реестры для наёмного и собственного транспорта</label>
        <label class="rs__check"><input v-model="settings.notify_enabled" type="checkbox"> Уведомления в Telegram (неготовность, согласование)</label>
        <label>Telegram chat id получателей (через запятую)
          <input v-model="chatIds" placeholder="руководитель делопроизводства, операционный директор">
        </label>
        <label class="rs__wide">Праздничные дни для расчёта рабочих дней (через запятую, ГГГГ-ММ-ДД)
          <input v-model="holidays" placeholder="2026-11-04, 2027-01-01">
        </label>
      </div>
      <button v-if="canEdit && settings" class="rs__btn rs__btn--primary" type="button" @click="saveSettings">Сохранить настройки</button>
    </section>
  </div>
</template>

<script>
import { computed, defineComponent, h, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { registryApi, errorText } from '@/backend/registryApi.js'
import { DAY_KINDS, SOURCE_LABELS, emptyTerms, formatDate, termsText } from './registryLogic.mjs'

const DaysField = defineComponent({
  props: { days: [Number, String], kind: String, label: String },
  emits: ['update:days', 'update:kind'],
  setup(props, { emit }) {
    return () => h('label', {}, [
      props.label,
      h('span', { class: 'rs__row' }, [
        h('input', { type: 'number', min: 0, value: props.days, style: 'width:80px',
          onInput: (e) => emit('update:days', Number(e.target.value)) }),
        h('select', { value: props.kind, onChange: (e) => emit('update:kind', e.target.value) },
          DAY_KINDS.map((k) => h('option', { value: k.value, selected: k.value === props.kind }, k.label))),
      ]),
    ])
  },
})

const WEEKDAYS = ['понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота', 'воскресенье']

export default {
  name: 'RegistrySettingsPage',
  components: { DaysField },
  setup() {
    const store = useStore()
    const route = useRoute()
    const roleName = computed(() => store.state.user.user?.role?.name)
    const canEdit = computed(() => ['superadmin', 'Админ'].includes(roleName.value) || store.getters.hasPermission('registry.edit'))
    const customers = ref([])
    const customerId = ref(null)
    const terms = ref([])
    const calendar = ref([])
    const settings = ref(null)
    const chatIds = ref('')
    const holidays = ref('')
    const draft = ref(emptyTerms(null))
    const nthChoice = ref('1')
    const manual = ref({ last_registry_date: '', payment_date: '' })
    const error = ref('')
    const message = ref('')
    const showAll = ref(false)
    const fileInput = ref(null)
    const todayIso = new Date().toISOString().slice(0, 10)
    const upcoming = computed(() => showAll.value ? calendar.value : calendar.value.filter((r) => r.payment_date >= todayIso))
    const draftTerm = computed(() => (terms.value[0] ? terms.value[0].payment_days : 75))

    async function loadCustomer() {
      error.value = ''
      draft.value = emptyTerms(customerId.value)
      ;[terms.value, calendar.value] = await Promise.all([registryApi.terms(customerId.value), registryApi.calendar(customerId.value)])
    }

    async function guard(action, okText) {
      error.value = ''
      message.value = ''
      try {
        const result = await action()
        message.value = typeof okText === 'function' ? okText(result) : okText
        await loadCustomer()
      } catch (e) {
        error.value = errorText(e)
      }
    }

    const addTerms = () => guard(() => {
      const payload = { ...draft.value, customer_id: customerId.value }
      payload.payment_rule = nthChoice.value === 'last' ? null
        : { nth: nthChoice.value.split(',').map(Number), weekday: draft.value.payment_rule.weekday }
      return registryApi.addTerms(payload)
    }, 'Редакция условий сохранена')

    const importFile = (event) => {
      const file = event.target.files[0]
      if (!file) return
      guard(() => registryApi.importCalendar(customerId.value, file),
        (r) => `Загружено из файла: ${r.rows_in_file} дат, новых — ${r.added}`)
      if (fileInput.value) fileInput.value.value = ''
    }
    const addDay = () => guard(() => registryApi.addPaymentDay({ customer_id: customerId.value, ...manual.value }), 'Дата добавлена')
    const removeDay = (row) => guard(() => registryApi.deletePaymentDay(row.id), 'Дата удалена')

    async function saveSettings() {
      const patch = { ...settings.value }
      patch.notify_chat_ids = chatIds.value.split(',').map((s) => s.trim()).filter(Boolean)
      patch.holidays = holidays.value.split(',').map((s) => s.trim()).filter(Boolean)
      try {
        settings.value = await registryApi.saveSettings(patch)
        message.value = 'Настройки сохранены'
      } catch (e) {
        error.value = errorText(e)
      }
    }

    onMounted(async () => {
      try {
        const [list, s] = await Promise.all([registryApi.customers(), registryApi.settings()])
        customers.value = list
        settings.value = s
        chatIds.value = (s.notify_chat_ids || []).join(', ')
        holidays.value = (s.holidays || []).join(', ')
        const wanted = Number(route.query.customer)
        const preferred = list.find((c) => c.id === wanted) || list.find((c) => c.has_terms)
          || list.find((c) => /эфес|efes/i.test(c.name)) || list[0]
        customerId.value = preferred ? preferred.id : null
        if (customerId.value) await loadCustomer()
      } catch (e) {
        error.value = errorText(e)
      }
    })

    return {
      SOURCE_LABELS, WEEKDAYS, addDay, addTerms, calendar, canEdit, chatIds, customerId, customers, draft, draftTerm,
      error, fileInput, formatDate, holidays, importFile, loadCustomer, manual, message, nthChoice, removeDay,
      saveSettings, settings, showAll, terms, termsText, upcoming,
    }
  },
}
</script>

<style scoped>
.rs { padding: 24px 20px 80px; display: flex; flex-direction: column; gap: 16px; color: #111827; max-width: 1200px; }
.rs__head { display: flex; justify-content: space-between; align-items: flex-end; gap: 12px; flex-wrap: wrap; }
.rs__back { color: #4f46e5; font-size: 0.85rem; text-decoration: none; }
.rs__title { margin: 4px 0 0; font-size: 1.4rem; }
.rs__card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px; background: #fff; display: flex; flex-direction: column; gap: 10px; }
.rs__h2 { margin: 0; font-size: 1.05rem; }
.rs__h3 { margin: 0 0 8px; font-size: 0.95rem; }
.rs__muted { color: #6b7280; font-size: 0.85rem; margin: 0; }
.rs__input { border: 1px solid #d1d5db; border-radius: 8px; padding: 6px 8px; font: inherit; font-size: 0.85rem; }
.rs__table { border-collapse: collapse; width: 100%; font-size: 0.82rem; }
.rs__table th { text-align: left; background: #f9fafb; padding: 6px; border-bottom: 1px solid #e5e7eb; }
.rs__table td { padding: 6px; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
.rs__form { border-top: 1px dashed #e5e7eb; padding-top: 12px; }
.rs__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 12px; margin-bottom: 10px; }
.rs__grid :deep(label) { display: flex; flex-direction: column; gap: 4px; font-size: 0.8rem; color: #374151; }
.rs__grid :deep(input), .rs__grid :deep(select) { border: 1px solid #d1d5db; border-radius: 6px; padding: 5px 7px; font: inherit; font-size: 0.85rem; }
:deep(.rs__row) { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.rs__check { flex-direction: row !important; align-items: center; gap: 6px !important; }
.rs__wide { grid-column: 1 / -1; }
.rs__btn { align-self: flex-start; border: 1px solid #d1d5db; background: #fff; border-radius: 9999px; padding: 7px 14px; font: inherit; font-size: 0.85rem; cursor: pointer; }
.rs__btn:disabled { opacity: 0.5; }
.rs__btn--primary { background: #4f46e5; border-color: #4f46e5; color: #fff; }
.rs__btn--sm { padding: 3px 10px; font-size: 0.78rem; }
.rs__calendar { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 6px; }
.rs__day { display: flex; justify-content: space-between; gap: 8px; align-items: center; border: 1px solid #f3f4f6; border-radius: 8px; padding: 5px 8px; font-size: 0.82rem; }
.rs__term { color: #6b7280; font-size: 0.75rem; }
.rs__term--odd { color: #b45309; font-weight: 600; }
.rs__x { border: 0; background: none; color: #9ca3af; cursor: pointer; }
.rs__error { border: 1px solid #fecaca; background: #fef2f2; color: #7f1d1d; border-radius: 10px; padding: 10px 14px; }
.rs__ok { border: 1px solid #bbf7d0; background: #f0fdf4; color: #166534; border-radius: 10px; padding: 10px 14px; }
</style>
